
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, TextField, Button, Alert, CircularProgress, Stack } from '@mui/material';
import { Line } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { fetchWithDemoFallback } from '../../../helpers/apiHelper';

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, TimeScale, Filler);

export default function ChartPage() {
  const [rawTx, setRawTx] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [outputRows, setOutputRows] = useState([]);
  const [drillDivision, setDrillDivision] = useState(null);
  const [drillDivisionKey, setDrillDivisionKey] = useState(null);
  const [catDivisionFilter, setCatDivisionFilter] = useState(null);
  const [catGroupFilter, setCatGroupFilter] = useState(null);
  const [viewBy, setViewBy] = useState(() => {
    try {
      const stored = localStorage.getItem('chart.viewBy');
      return stored || 'final_category';
    } catch (e) {
      return 'final_category';
    }
  });
  const [xMin, setXMin] = useState('');
  const [xMax, setXMax] = useState('');
  const [datesLoaded, setDatesLoaded] = useState(false);
  const [stacked, setStacked] = useState(() => {
    try {
      const stored = localStorage.getItem('chart.stacked');
      return stored === null ? true : stored === 'true';
    } catch (e) {
      return true;
    }
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [budgetData, setBudgetData] = useState(null);
  const [categoriesData, setCategoriesData] = useState([]);

  const API_BASE =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
    process.env.REACT_APP_API_BASE_URL ||
    'http://localhost:3001';

  /* -------------------- Helpers -------------------- */

  // Strict ISO date check: only accept complete YYYY-MM-DD
  const isCompleteISODate = (value) => {
    if (typeof value !== 'string') return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const d = new Date(value);
    return !Number.isNaN(d.getTime());
  };

  const parseDateValue = useCallback((value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const d = new Date(`${raw}T00:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      const [dd, mm, yyyy] = raw.split('/');
      const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (/^\d{8}$/.test(raw)) {
      const yyyy = raw.slice(0, 4);
      const mm = raw.slice(4, 6);
      const dd = raw.slice(6, 8);
      const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }, []);

  const safeParseDate = useCallback((obj) => {
    const candidates = [
      obj.transactiondate_iso,
      obj.transactiondate,
      obj.transaction_date,
      obj.transactionDate,
      obj.date,
      obj.Transactiedatum,
      obj.Date,
      obj.date_added,
      obj.createdAt,
      obj.transactionDate, // just in case
      obj.transaction_date // just in case
    ].filter(Boolean);

    for (const c of candidates) {
      const d = parseDateValue(c);
      if (d) return d;
    }
    return null;
  }, [parseDateValue]);

  const safeParseAmount = (obj) => {
    const candidates = [obj.amount, obj.Amount, obj.Transactiebedrag, obj.transaction_amount];

    for (const c of candidates) {
      if (c === null || c === undefined || c === '') continue;
      const n = typeof c === 'number' ? c : Number(String(c).replace(',', '.'));
      if (!Number.isNaN(n)) return n;
    }
    return null;
  };

  const safeClassification = (obj) => {
    const c = obj.final_category ?? obj.manual_category ?? obj.auto_category ?? obj.classification ?? obj.Classification ?? obj.category ?? obj.Category;
    const s = String(c ?? '').trim();
    return s ? s : 'Unclassified';
  };

  const safeAggregationBucket = useCallback((obj, mode) => {
    if (mode === 'division') {
      const value = String(obj.division ?? '').trim();
      return value || 'Unassigned Division';
    }

    if (mode === 'group') {
      const value = String(obj.group ?? obj.group_name ?? '').trim();
      return value || 'Unassigned Group';
    }

    return safeClassification(obj);
  }, []);

  const normalizeBucketKey = (value) => {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  };

  
const safeExclude = (obj) => {
  const raw = obj.exclude ?? obj.Exclude;
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
};


  /* -------------------- Fetch -------------------- */

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError('');
    setChartData(null);
    setOutputRows([]);

    try {
      const [txRes, budgetRes, catRes] = await Promise.all([
        fetchWithDemoFallback(`${API_BASE}/api/transactions`, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetchWithDemoFallback(`${API_BASE}/api/budgets`, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }
        }).catch(() => null),
        fetchWithDemoFallback(`${API_BASE}/api/categories-with-groups`, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }
        }).catch(() => null)
      ]);

      if (!txRes.ok) throw new Error(`Failed to fetch transactions (HTTP ${txRes.status})`);
      const data = await txRes.json();
      if (!Array.isArray(data)) throw new Error('API did not return an array');

      console.log('Chart: Fetched', data.length, 'transactions, excluded count:', data.filter(d => d.exclude).length);
      setRawTx(data);

      if (budgetRes && budgetRes.ok) {
        const budget = await budgetRes.json();
        setBudgetData(budget || null);
      }

      if (catRes && catRes.ok) {
        const cats = await catRes.json();
        setCategoriesData(Array.isArray(cats) ? cats : []);
      }
    } catch (e) {
      setRawTx([]);
      setError(e?.message || 'Unknown error fetching transactions');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);


useEffect(() => {
  if (rawTx.length) {
    console.log('Sample transaction from API:', rawTx[0]);
    console.log('Keys:', Object.keys(rawTx[0]));
  }
}, [rawTx]);


  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Load persisted dates from the backend on mount
  useEffect(() => {
    Promise.all([
      fetchWithDemoFallback(`${API_BASE}/api/settings/chart.xMin`).then((r) => r.json()).catch(() => ({})),
      fetchWithDemoFallback(`${API_BASE}/api/settings/chart.xMax`).then((r) => r.json()).catch(() => ({}))
    ]).then(([minRes, maxRes]) => {
      if (minRes.value) setXMin(minRes.value);
      if (maxRes.value) setXMax(maxRes.value);
      setDatesLoaded(true);
    });
  }, [API_BASE]);

  useEffect(() => {
    const handleTransactionsUpdated = (e) => {
      console.log('Chart: transactionsUpdated event received, refetching...', e?.detail);
      const detail = e?.detail || {};
      const { id, exclude } = detail;

      // Optimistically update local copy so chart reflects change immediately
      if (id && typeof exclude === 'boolean') {
        setRawTx((prev) =>
          Array.isArray(prev) ? prev.map((item) => (String(item?._id ?? item?.id) === id ? { ...item, exclude } : item)) : prev
        );
      }

      fetchTransactions();
    };

    window.addEventListener('transactionsUpdated', handleTransactionsUpdated);
    return () => window.removeEventListener('transactionsUpdated', handleTransactionsUpdated);
  }, [fetchTransactions]);

  /* -------------------- Parse -------------------- */

  const parsedTransactions = useMemo(() => {
    const activeDrillDivisionKey = normalizeBucketKey(drillDivisionKey || drillDivision);
    const activeCatDivisionKey = normalizeBucketKey(catDivisionFilter);
    const activeCatGroupKey = normalizeBucketKey(catGroupFilter);

    const tx = rawTx
      .map((r) => {
        const date = safeParseDate(r);
        const amount = safeParseAmount(r);
        const bucket = safeAggregationBucket(r, viewBy);
        const divisionBucket = safeAggregationBucket(r, 'division');
        const divisionBucketKey = normalizeBucketKey(divisionBucket);
        const groupBucket = safeAggregationBucket(r, 'group');
        const groupBucketKey = normalizeBucketKey(groupBucket);
        const exclude = safeExclude(r);

        if (!date || amount === null || exclude || amount >= 0) return null;

        if (viewBy === 'group' && activeDrillDivisionKey && divisionBucketKey !== activeDrillDivisionKey) return null;

        if (viewBy === 'final_category') {
          if (activeCatDivisionKey && divisionBucketKey !== activeCatDivisionKey) return null;
          if (activeCatGroupKey && groupBucketKey !== activeCatGroupKey) return null;
        }

        // Keep signed amount so rolling totals reflect both income and expenses.
      return { date, bucket, amount, fixVariable: String(r.fix_variable ?? '').trim(), category: safeClassification(r) };
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);

    console.log('Chart: Parsed', tx.length, 'transactions (after filtering excluded)');
    return tx;
  }, [rawTx, viewBy, drillDivision, drillDivisionKey, catDivisionFilter, catGroupFilter, safeParseDate, safeAggregationBucket]);

  // Data range to initialise & clamp date inputs (prevents Chart.js time-scale crash)
  const dataRange = useMemo(() => {
    if (!parsedTransactions.length) return { start: null, end: null };
    const start = parsedTransactions[0].date;
    const end = parsedTransactions[parsedTransactions.length - 1].date;
    return { start, end };
  }, [parsedTransactions]);

  /* -------------------- Build chart -------------------- */

  useEffect(() => {
    // Start with buckets from transactions
    const txBuckets = new Set(parsedTransactions.map((t) => t.bucket));
    
    // Add categories with fixed amounts even if they have no transactions
    const fixedCategoryBuckets = new Set();
    categoriesData.forEach((c) => {
      const catName = String(c.category_name ?? '').trim();
      const fixVar = String(c.fix_variable ?? '').trim();
      const numAmount = Number(fixVar);
      
      // If it's a non-zero number, add it
      if (!Number.isNaN(numAmount) && numAmount !== 0) {
        fixedCategoryBuckets.add(catName);
      }
    });
    
    const buckets = [...txBuckets, ...fixedCategoryBuckets];
    
    // If we have no buckets at all, nothing to chart
    if (!buckets.length) {
      setChartData(null);
      setOutputRows([]);
      return;
    }


    // Use user-selected dates if available, otherwise fall back to data range
    let chartStartDate = dataRange.start;
    let chartEndDate = dataRange.end;
    
    // If no transactions but we have fixed categories, use last 12 months
    if (!chartStartDate || !chartEndDate) {
      const today = new Date();
      chartEndDate = new Date(today);
      chartStartDate = new Date(today);
      chartStartDate.setFullYear(chartStartDate.getFullYear() - 1);
    }
    
    if (isCompleteISODate(xMin)) {
      const userMin = new Date(xMin);
      if (chartStartDate && userMin >= chartStartDate) chartStartDate = userMin;
    }
    if (isCompleteISODate(xMax)) {
      // Always use user's xMax, even if it's beyond the data range
      // This allows the chart to show the selected range with empty space if needed
      chartEndDate = new Date(xMax);
    }

    const startDate = chartStartDate;
    const endDate = chartEndDate;

    // Initialise date inputs once (only if no saved value was loaded)
    const startISO = startDate.toISOString().slice(0, 10);
    const endISO = endDate.toISOString().slice(0, 10);
    if (datesLoaded) {
      setXMin((prev) => prev || startISO);
      setXMax((prev) => prev || endISO);
    }

    // Generate daily ticks across full dataset so each date has a 30-day rolling total.
    const dailyDates = [];
    let current = new Date(startDate);
    current.setUTCHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setUTCHours(0, 0, 0, 0);

    while (current <= end) {
      dailyDates.push(new Date(current));
      current.setUTCDate(current.getUTCDate() + 1);
    }

    const ROLLING_DAYS = 30;

    // Build map of category name → fixed amount (if numeric) or rolling (if "Variable")
    const categoryFixedAmounts = new Map();
    const fixedCategorySet = new Set();
    
    categoriesData.forEach((c) => {
      const catName = String(c.category_name ?? '').trim();
      const fixVar = String(c.fix_variable ?? '').trim();
      
      if (!catName) return;
      
      // If it's a number, store the amount
      // If it's a number (positive income or negative expense), store it
      const numAmount = Number(fixVar);
      if (!Number.isNaN(numAmount) && numAmount !== 0) {
        categoryFixedAmounts.set(catName, numAmount);
        fixedCategorySet.add(catName);
      }
    });

    const out = dailyDates.map((dayDate) => {
      const cutoff = new Date(dayDate);
      cutoff.setDate(cutoff.getDate() - (ROLLING_DAYS - 1));
      const monthKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}`;

      const row = { Date: dayDate.toISOString().slice(0, 10) };

      buckets.forEach((bucket) => {
        if (viewBy === 'final_category') {
          // Bucket IS the category — use budget if fixed, rolling if variable.
          if (fixedCategorySet.has(bucket)) {
            // Use fixed amount from category table if available
            const fixedAmount = categoryFixedAmounts.get(bucket);
            if (fixedAmount !== undefined) {
              // For spending chart: expenses are negative, show as positive; income is positive, show as 0
              row[bucket] = Number(Math.max(-Number(fixedAmount), 0).toFixed(2));
              return;
            }
            // Fallback to budget data
            const raw = budgetData?.values?.[bucket]?.[monthKey];
            if (raw !== undefined && raw !== null) {
              row[bucket] = Number(Math.max(-Number(raw), 0).toFixed(2));
              return;
            }
          }
          const netTotal = parsedTransactions
            .filter((t) => t.bucket === bucket && t.date >= cutoff && t.date <= dayDate)
            .reduce((sum, t) => sum + t.amount, 0);
          row[bucket] = Number(Math.max(-netTotal, 0).toFixed(2));
        } else {
          // division or group: each category inside is handled independently.
          // Fixed categories → monthly budget value (flat, no rolling).
          // Variable categories → 30-day rolling actual spend.
          // Bucket total = sum of both.

          const fixedCatsInBucket = new Set(
            parsedTransactions
              .filter((t) => t.bucket === bucket && fixedCategorySet.has(t.category))
              .map((t) => t.category)
          );

          let fixedTotal = 0;
          fixedCatsInBucket.forEach((cat) => {
            // Use fixed amount from category table if available
            const fixedAmount = categoryFixedAmounts.get(cat);
            if (fixedAmount !== undefined) {
              // For spending chart: expenses are negative, add as positive; income is positive, add as 0
              fixedTotal += Math.max(-Number(fixedAmount), 0);
            } else {
              // Fallback to budget data
              const raw = budgetData?.values?.[cat]?.[monthKey];
              if (raw !== undefined && raw !== null) {
                fixedTotal += Math.max(-Number(raw), 0);
              }
            }
          });

          const variableRolling = parsedTransactions
            .filter((t) => t.bucket === bucket && !fixedCategorySet.has(t.category) && t.date >= cutoff && t.date <= dayDate)
            .reduce((sum, t) => sum + t.amount, 0);

          row[bucket] = Number((fixedTotal + Math.max(-variableRolling, 0)).toFixed(2));
        }
      });

      return row;
    });

    // Sort buckets by value on the selected end date (last day of visible window).
    const sortStartISO = startDate.toISOString().slice(0, 10);
    const sortEndISO = endDate.toISOString().slice(0, 10);
    const selectedEndISO = isCompleteISODate(xMax)
      ? (xMax < sortStartISO ? sortStartISO : xMax > sortEndISO ? sortEndISO : xMax)
      : sortEndISO;
    const selectedEndRow = out.find((row) => row.Date === selectedEndISO) || out[out.length - 1] || {};

    const bucketTotals = buckets
      .map((bucket) => ({
        bucket,
        total: Number(selectedEndRow[bucket] || 0)
      }))
      .sort((a, b) => b.total - a.total);

    const colours = [
      '#1976d2',
      '#ff9800',
      '#4caf50',
      '#e91e63',
      '#9c27b0',
      '#00bcd4',
      '#8bc34a',
      '#ffc107'
    ];

    const fillColours = [
      'rgba(25, 118, 210, 0.45)',
      'rgba(255, 152, 0, 0.45)',
      'rgba(76, 175, 80, 0.45)',
      'rgba(233, 30, 99, 0.45)',
      'rgba(156, 39, 176, 0.45)',
      'rgba(0, 188, 212, 0.45)',
      'rgba(139, 195, 74, 0.45)',
      'rgba(255, 193, 7, 0.45)'
    ];

    const datasets = bucketTotals.map((item, index) => ({
      label: item.bucket,
      bucketKey: normalizeBucketKey(item.bucket),
      data: out.map((r) => ({
        x: new Date(r.Date),
        y: r[item.bucket] ?? 0
      })),
      borderColor: colours[index % colours.length],
      backgroundColor: fillColours[index % fillColours.length],
      borderWidth: stacked ? 1.5 : 2,
      pointRadius: 0,
      pointHitRadius: 16,
      pointHoverRadius: 2,
      tension: 0.2,
      fill: stacked,
      order: index,
      ...(stacked ? { stack: 'aggregations' } : {})
    }));

    setOutputRows(out);
    setChartData({ datasets });
  }, [parsedTransactions, dataRange, viewBy, xMin, xMax, datesLoaded, budgetData, categoriesData, stacked]);

  /* -------------------- Crash-proof min/max -------------------- */

  const clampDate = (d, min, max) => {
    if (!d || !min || !max) return d;
    if (d < min) return min;
    if (d > max) return max;
    return d;
  };

  const rawMin = isCompleteISODate(xMin) ? new Date(xMin) : null;
  const rawMax = isCompleteISODate(xMax) ? new Date(xMax) : null;

  const clampedMin = rawMin && dataRange.start ? clampDate(rawMin, dataRange.start, dataRange.end) : null;
  const clampedMax = rawMax && dataRange.end ? clampDate(rawMax, dataRange.start, dataRange.end) : null;

  // Only pass min/max when both exist AND min <= max; otherwise let Chart.js auto-scale
  const finalMin = clampedMin && clampedMax && clampedMin <= clampedMax ? clampedMin : undefined;
  const finalMax = clampedMin && clampedMax && clampedMin <= clampedMax ? clampedMax : undefined;

  const divisionOptions = useMemo(() => {
    return Array.from(
      new Set(
        rawTx
          .map((tx) => String(tx?.division ?? '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rawTx]);

  // Group options for the Final Category filter, cascading on the selected division
  // filter (if any) so only groups that actually exist within it are offered.
  const catGroupOptions = useMemo(() => {
    const activeCatDivisionKey = normalizeBucketKey(catDivisionFilter);
    const source = activeCatDivisionKey
      ? rawTx.filter((tx) => normalizeBucketKey(String(tx?.division ?? '').trim()) === activeCatDivisionKey)
      : rawTx;
    return Array.from(
      new Set(
        source
          .map((tx) => String(tx?.group ?? tx?.group_name ?? '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rawTx, catDivisionFilter]);

  /* -------------------- Export -------------------- */

  const downloadRM = () => {
    if (!outputRows?.length) return;
    const sheet = XLSX.utils.json_to_sheet(outputRows);
    const workbook = XLSX.utils.book_new();
    const sheetName =
      viewBy === 'division'
        ? 'Rolling30Division'
        : viewBy === 'group'
          ? 'Rolling30Group'
          : 'Rolling30FinalCategory';
    const fileName =
      viewBy === 'division'
        ? 'rolling30_division.xlsx'
        : viewBy === 'group'
          ? 'rolling30_group.xlsx'
          : 'rolling30_final_category.xlsx';
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    XLSX.writeFile(workbook, fileName);
  };

  /* -------------------- Render -------------------- */

  return (
    <Box
      width="100vw"
      height="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      padding="20px"
      boxSizing="border-box"
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', maxWidth: 1200 }}>
        <h1 style={{ margin: 0 }}>Spending Profile</h1>

        <Button variant="contained" onClick={fetchTransactions} disabled={loading}>
          Refresh
        </Button>

        <Button variant="outlined" onClick={downloadRM} disabled={!outputRows.length}>
          Download
        </Button>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
          <Box sx={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>View by</Box>
          <Button
            size="small"
            variant={viewBy === 'division' ? 'contained' : 'outlined'}
            onClick={() => {
              setViewBy('division');
              setDrillDivision(null);
              setDrillDivisionKey(null);
              try { localStorage.setItem('chart.viewBy', 'division'); } catch (e) {}
            }}
            sx={{ minWidth: 90 }}
          >
            Division
          </Button>
          <Button
            size="small"
            variant={viewBy === 'group' ? 'contained' : 'outlined'}
            onClick={() => {
              setViewBy('group');
              try { localStorage.setItem('chart.viewBy', 'group'); } catch (e) {}
            }}
            sx={{ minWidth: 80 }}
          >
            Group
          </Button>
          <Button
            size="small"
            variant={viewBy === 'final_category' ? 'contained' : 'outlined'}
            onClick={() => {
              setViewBy('final_category');
              setDrillDivision(null);
              setDrillDivisionKey(null);
              try { localStorage.setItem('chart.viewBy', 'final_category'); } catch (e) {}
            }}
            sx={{ minWidth: 130 }}
          >
            Final Category
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>Chart type</Box>
          <Button
            size="small"
            variant={stacked ? 'contained' : 'outlined'}
            onClick={() => {
              setStacked(true);
              try { localStorage.setItem('chart.stacked', 'true'); } catch (e) {}
            }}
            sx={{ minWidth: 80 }}
          >
            Stacked
          </Button>
          <Button
            size="small"
            variant={!stacked ? 'contained' : 'outlined'}
            onClick={() => {
              setStacked(false);
              try { localStorage.setItem('chart.stacked', 'false'); } catch (e) {}
            }}
            sx={{ minWidth: 80 }}
          >
            Lines
          </Button>
        </Stack>
      </Stack>

      {viewBy === 'group' && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ width: '100%', maxWidth: 1200, mt: 2 }}
        >
          <TextField
            select
            size="small"
            label="Division"
            value={drillDivision || ''}
            onChange={(e) => {
              const nextDivision = String(e.target.value || '').trim();
              setDrillDivision(nextDivision || null);
              setDrillDivisionKey(nextDivision ? normalizeBucketKey(nextDivision) : null);
              setViewBy('group');
              try { localStorage.setItem('chart.viewBy', 'group'); } catch (err) {}
            }}
            sx={{ minWidth: 280, bgcolor: '#fff' }}
            SelectProps={{ native: true }}
          >
            <option value="">Select a division</option>
            {divisionOptions.map((division) => (
              <option key={division} value={division}>
                {division}
              </option>
            ))}
          </TextField>
        </Stack>
      )}

      {viewBy === 'group' && drillDivision && (
        <Alert severity="info" sx={{ mt: 2, width: '100%', maxWidth: 1200 }}>
          Showing groups for division: {drillDivision}
        </Alert>
      )}

      {viewBy === 'group' && !drillDivision && (
        <Alert severity="info" sx={{ mt: 2, width: '100%', maxWidth: 1200 }}>
          Choose a division from the dropdown to see its group breakdown.
        </Alert>
      )}

      {viewBy === 'final_category' && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ width: '100%', maxWidth: 1200, mt: 2 }}
        >
          <TextField
            select
            size="small"
            label="Division"
            value={catDivisionFilter || ''}
            onChange={(e) => {
              const nextDivision = String(e.target.value || '').trim();
              setCatDivisionFilter(nextDivision || null);
              // Group filter may no longer apply to the newly selected division.
              setCatGroupFilter(null);
            }}
            sx={{ minWidth: 280, bgcolor: '#fff' }}
            SelectProps={{ native: true }}
          >
            <option value="">All divisions</option>
            {divisionOptions.map((division) => (
              <option key={division} value={division}>
                {division}
              </option>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Group"
            value={catGroupFilter || ''}
            onChange={(e) => {
              const nextGroup = String(e.target.value || '').trim();
              setCatGroupFilter(nextGroup || null);
            }}
            sx={{ minWidth: 280, bgcolor: '#fff' }}
            SelectProps={{ native: true }}
          >
            <option value="">All groups</option>
            {catGroupOptions.map((group) => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </TextField>

          {(catDivisionFilter || catGroupFilter) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setCatDivisionFilter(null);
                setCatGroupFilter(null);
              }}
            >
              Clear filters
            </Button>
          )}
        </Stack>
      )}

      {viewBy === 'final_category' && (catDivisionFilter || catGroupFilter) && (
        <Alert severity="info" sx={{ mt: 2, width: '100%', maxWidth: 1200 }}>
          Showing final categories
          {catDivisionFilter ? ` for division: ${catDivisionFilter}` : ''}
          {catGroupFilter ? `${catDivisionFilter ? ' /' : ' for'} group: ${catGroupFilter}` : ''}
        </Alert>
      )}

      {loading && (
        <Box mt={2} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} />
          Loading transactions from local database…
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: 1200 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && !parsedTransactions.length && (
        <Alert severity="info" sx={{ mt: 2, width: '100%', maxWidth: 1200 }}>
          No transactions found (or no valid date/amount fields).
        </Alert>
      )}

      {/* Date range controls */}
      <Box
        display="flex"
        gap={2}
        mt={2}
        bgcolor="#fff"
        padding="10px"
        borderRadius="8px"
        sx={{ width: '100%', maxWidth: 1200 }}
      >
        <TextField
          label="Start Date"
          type="date"
            value={xMin}
            onChange={(e) => {
              const v = e.target.value;
              setXMin(v);
              fetch(`${API_BASE}/api/settings/chart.xMin`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: v }) }).catch(() => {});
            }}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />

        <TextField
          label="End Date"
          type="date"
            value={xMax}
            onChange={(e) => {
              const v = e.target.value;
              setXMax(v);
              fetch(`${API_BASE}/api/settings/chart.xMax`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: v }) }).catch(() => {});
            }}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </Box>

      <Box width="100%" maxWidth={1200} height="75vh" mt={2}>
        {chartData && (
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: { legend: { display: true, position: 'top' } },
              scales: {
                x: {
                  type: 'time',
                  time: { unit: 'day' },
                  min: finalMin,
                  max: finalMax,
                  stacked,
                  title: { display: true, text: 'Date' }
                },
                y: {
                  stacked,
                  beginAtZero: true,
                  title: {
                    display: true,
                    text:
                      viewBy === 'division'
                        ? '30-day rolling expenses by division'
                        : viewBy === 'group'
                          ? (drillDivision
                              ? `30-day rolling expenses by group (${drillDivision})`
                              : '30-day rolling expenses by group')
                          : '30-day rolling expenses by final category'
                  }
                }
              }
            }}
          />
        )}
      </Box>
    </Box>
  );
}
